import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import api from '../../services/api';

const RACING_FORM_TYPES = [
  { value: 'pdf_racing_form_ragozin', label: 'Ragozin' },
  { value: 'pdf_racing_form_beyer', label: 'Beyer' },
  { value: 'pdf_racing_form_thoroughmanager', label: 'Thoroughmanager' },
  { value: 'pdf_racing_form_lifetime_pp', label: 'Lifetime Past Performance' },
];

const RacingFormDialog = ({ open, onClose, onSuccess, horse }) => {
  const [numbersType, setNumbersType] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [notes, setNotes] = useState('');
  const [uploading, setUploading] = useState(false);

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!numbersType) {
      toast.error('Please select a numbers type');
      return;
    }

    if (!selectedFile) {
      toast.error('Please select a file to upload');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('horse_id', horse.id);
      formData.append('description', `${RACING_FORM_TYPES.find(t => t.value === numbersType)?.label || 'Racing Form'}`);
      formData.append('category', numbersType);
      if (notes) {
        formData.append('notes', notes);
      }

      await api.post('/files/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      // Reset form
      setNumbersType('');
      setSelectedFile(null);
      setNotes('');
      
      onSuccess();
    } catch (error) {
      console.error('Error uploading racing form:', error);
      toast.error('Failed to upload racing form');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (!uploading) {
      setNumbersType('');
      setSelectedFile(null);
      setNotes('');
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Upload Racing Form</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Numbers Type */}
          <div className="space-y-2">
            <Label>Numbers Type *</Label>
            <Select value={numbersType} onValueChange={setNumbersType}>
              <SelectTrigger>
                <SelectValue placeholder="Select numbers type" />
              </SelectTrigger>
              <SelectContent className="z-[100]">
                {RACING_FORM_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* File Upload */}
          <div className="space-y-2">
            <Label>File Upload *</Label>
            <div className="flex items-center gap-2">
              <Input
                type="text"
                value={selectedFile?.name || ''}
                placeholder="..."
                disabled
                className="flex-1"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => document.getElementById('racing-form-file-input').click()}
              >
                Browse...
              </Button>
            </div>
            <input
              id="racing-form-file-input"
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional notes..."
              rows={4}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={uploading}>
              Cancel
            </Button>
            <Button type="submit" disabled={uploading}>
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default RacingFormDialog;
